/*
 * Mercado — search dropdown for the Lushy theme
 * ---------------------------------------------
 * Shopify's Predictive Search API (/search/suggest) rejects Arabic buyer
 * sessions with HTTP 417, and Arabic is this store's default storefront
 * language. The dropdown therefore uses STOREFRONT search (/search) rendered
 * through sections/mc-search-results.liquid. Same engine as pressing Enter.
 *
 * Query layer (vocabulary lives in assets/smart-search-config.js):
 *  - Arabic normalisation (hamza/alef forms, taa marbuta, alef maqsura,
 *    diacritics, tatweel).
 *  - Synonyms: Arabic, transliterated and misspelled words → catalogue words.
 *  - Fallback candidates (full query, query minus last word, longest word)
 *    instead of an empty panel.
 *  - When a fallback found results, pressing Enter searches that wording too.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.resolved = null;
    this.isOpen = false;
    this.requestId = 0;

    if (!this.input || !this.predictiveSearchResults) return;
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 220).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  /* ----------------------------- query layer ----------------------------- */

  get config() {
    return window.SmartSearchConfig || { SYNONYMS: {}, maxQueryLength: 64 };
  }

  normalize(term) {
    return (term || '')
      .toLowerCase()
      .replace(/[ً-ٰٟ]/g, '')
      .replace(/[آأإٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/ـ/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  expandQuery(term) {
    const synonyms = this.config.SYNONYMS || {};
    const normalized = this.normalize(term);
    if (synonyms[normalized]) return synonyms[normalized];

    let changed = false;
    const mapped = normalized.split(' ').map((word) => {
      if (synonyms[word]) { changed = true; return synonyms[word]; }
      return word;
    });
    return changed ? mapped.join(' ') : normalized;
  }

  buildCandidates(term) {
    const max = this.config.maxQueryLength || 64;
    const raw = this.normalize(term).slice(0, max);
    const candidates = [];
    const push = (value) => {
      if (value && value.length && !candidates.includes(value)) candidates.push(value);
    };

    push(term.trim().slice(0, max));
    push(this.expandQuery(raw));
    push(raw);

    const words = raw.split(' ').filter(Boolean);
    if (words.length > 1) {
      push(words.slice(0, -1).join(' '));
      push(words.slice().sort((a, b) => b.length - a.length)[0]);
    }
    return candidates;
  }

  searchUrl(term, prefix) {
    const params = new URLSearchParams();
    params.set('q', term);
    params.set('type', 'product');
    if (prefix) params.set('options[prefix]', 'last');
    params.set('section_id', 'mc-search-results');
    return `${(window.routes && window.routes.search_url) || '/search'}?${params.toString()}`;
  }

  fetchMarkup(term) {
    const tryOnce = (prefix) => fetch(this.searchUrl(term, prefix))
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-mc-search-results');
        return section ? section.innerHTML.trim() : '';
      });

    return tryOnce(true).catch(() => tryOnce(false)).catch(() => '');
  }

  countResults(markup) {
    const match = /data-mc-count="(\d+)"/.exec(markup || '');
    return match ? parseInt(match[1], 10) : 0;
  }

  escape(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  noteMarkup(used) {
    const template = this.dataset.noteText || 'Showing results for [terms]';
    return '<p class="mc-search-note">' + template.replace('[terms]', '<strong>' + this.escape(used) + '</strong>') + '</p>';
  }

  emptyMarkup(term) {
    const template = this.dataset.emptyText || 'No quick matches for [terms].';
    const seeAll = this.dataset.seeAllText || 'See all results';
    const href = this.searchUrl(term, true).replace(/&?section_id=mc-search-results/, '');
    return '<div class="mc-search-empty"><p>' + template.replace('[terms]', '<strong>' + this.escape(term) + '</strong>') +
      '</p><a class="link" href="' + this.escape(href) + '">' + this.escape(seeAll) + '</a></div>' +
      '<span class="hide" data-predictive-search-live-region-count-value>' + this.escape(template.replace('[terms]', term)) + '</span>';
  }

  /* -------------------------------- flow --------------------------------- */

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const term = this.getQuery();
    if (!term.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    /* If the dropdown only found results under another wording, search that. */
    if (this.resolved && this.resolved.term === term && this.resolved.used !== term) {
      this.input.value = this.resolved.used;
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;
    if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp': event.preventDefault(); this.switchOption('up'); break;
      case 'ArrowDown': event.preventDefault(); this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('[role="option"]');
    if (!allElements.length) return;
    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }
    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selected) selected.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = this.normalize(searchTerm);
    const requestId = ++this.requestId;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.resolved = this.cachedResults[queryKey].resolved;
      this.renderSearchResults(this.cachedResults[queryKey].markup);
      return;
    }

    const candidates = this.buildCandidates(searchTerm);
    const attempt = (index) => {
      if (requestId !== this.requestId) return; /* a newer keystroke won */

      if (index >= candidates.length) {
        const markup = this.emptyMarkup(searchTerm);
        this.cachedResults[queryKey] = { markup, resolved: null };
        this.resolved = null;
        this.renderSearchResults(markup);
        return;
      }

      const candidate = candidates[index];
      this.fetchMarkup(candidate).then((markup) => {
        if (requestId !== this.requestId) return;
        if (this.countResults(markup) > 0) {
          const changed = this.normalize(candidate) !== this.normalize(searchTerm);
          const finalMarkup = (changed ? this.noteMarkup(candidate) : '') + markup;
          const resolved = { term: searchTerm, used: candidate };
          this.cachedResults[queryKey] = { markup: finalMarkup, resolved };
          this.resolved = resolved;
          this.renderSearchResults(finalMarkup);
          return;
        }
        attempt(index + 1);
      });
    };
    attempt(0);
  }

  setLiveRegionLoadingState() {
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const counter = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (counter) this.setLiveRegionText(counter.textContent.trim());
  }

  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header');
    const bottom = header ? header.getBoundingClientRect().bottom : 0;
    this.resultsMaxHeight = Math.max(240, window.innerHeight - bottom);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.resolved = null;
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);